import { BusinessDate, eachDayOfRange } from '../date/business-date';
import { Hours } from '../shared/hours';
import type { WorkCalendar, WorkCalendarException } from './work-calendar';

/**
 * Capacidade de trabalho.
 *
 * Serviço de domínio **puro**: recebe o calendário já resolvido e devolve quartos de hora.
 * Não conhece funcionário, obra nem banco — o que o torna testável com uma tabela de
 * casos e reutilizável pelo planejamento semanal, pelo diário e pela conferência.
 *
 * Duas coisas que este arquivo existe para impedir:
 *
 * - **"45h por semana" como constante.** Semana com feriado vale 36h, e o sistema precisa
 *   saber disso sozinho (RN-042). Toda capacidade de período é uma soma de dias.
 * - **Dízima em soma de horas.** Tudo é inteiro de quarto de hora até a exibição
 *   (ADR-106); 9h ÷ 3 nunca vira 2,9999999.
 */

export interface ResolvedCalendar {
  readonly calendar: WorkCalendar;
  /** Exceções do calendário, indexadas por data. */
  readonly exceptions: ReadonlyMap<string, WorkCalendarException>;
}

/**
 * Resolve o calendário em cascata: funcionário → obra → padrão do sistema (RN-040).
 *
 * O primeiro que existir vence. É esta função que garante que o número 9 apareça em um
 * lugar só — o registro do calendário —, e não espalhado por quem calcula.
 */
export function resolveCalendar(
  candidates: readonly (WorkCalendar | undefined)[],
): WorkCalendar | undefined {
  return candidates.find((calendar) => calendar !== undefined);
}

/**
 * Capacidade de um dia, em quartos de hora.
 *
 * ```text
 * exceção da data, se existir       // feriado = 0, meio período = 16
 * senão jornada do dia da semana    // sexta de 8h numa semana de 9h
 * senão dia útil ? jornada padrão : 0
 * ```
 *
 * A exceção da **data** vence sempre — inclusive para **abrir** um sábado, que é o caso
 * do mutirão de concretagem (RN-041). Abaixo dela vem a jornada do **dia da semana**, que
 * é o que permite a semana de 44h: segunda a quinta 9h, sexta 8h.
 */
export function capacityOn(resolved: ResolvedCalendar, date: BusinessDate): number {
  const exception = resolved.exceptions.get(date.toString());
  if (exception !== undefined) return exception.quarterHours;

  const weekday = date.weekday();
  if (!resolved.calendar.workingWeekdays.includes(weekday)) return 0;

  return resolved.calendar.weekdayQuarterHours?.[weekday] ?? resolved.calendar.dailyQuarterHours;
}

/**
 * Capacidade de um intervalo — soma dia a dia, inclusive nas duas pontas.
 *
 * Nunca uma multiplicação: multiplicar a jornada pelo número de dias é exatamente o erro
 * que faz a semana do feriado prometer 45h de mão de obra que não existem (RN-042).
 */
export function capacityBetween(
  resolved: ResolvedCalendar,
  from: BusinessDate,
  to: BusinessDate,
): number {
  const days = eachDayOfRange(from, to);
  // Intervalo invertido é zero, não erro: quem pergunta a capacidade de um período vazio
  // recebe zero, e não um Result que toda tela teria de destrinchar.
  if (!days.ok) return 0;
  return days.value.reduce((total, day) => total + capacityOn(resolved, day), 0);
}

/** Dias com capacidade maior que zero no intervalo — o "quantos dias úteis" da tela. */
export function workingDaysBetween(
  resolved: ResolvedCalendar,
  from: BusinessDate,
  to: BusinessDate,
): number {
  const days = eachDayOfRange(from, to);
  if (!days.ok) return 0;
  return days.value.filter((day) => capacityOn(resolved, day) > 0).length;
}

export interface CapacitySummary {
  readonly daily: number;
  readonly weekly: number;
  readonly monthly: number;
  readonly workingDays: number;
}

/**
 * Capacidade do recurso a partir de uma data de referência.
 *
 * Semana = a semana ISO da data (segunda a domingo). Mês = o mês civil dela. As duas são
 * somas, pela razão acima.
 */
export function summarizeCapacity(
  resolved: ResolvedCalendar,
  reference: BusinessDate,
): CapacitySummary {
  const weekStart = reference.subtractDays(reference.weekday() - 1);
  const weekEnd = weekStart.addDays(6);

  const [year, month] = reference.toString().split('-').map(Number);
  const monthStart = BusinessDate.fromParts(year!, month!, 1);
  /* c8 ignore next -- o primeiro dia do mês da própria data é sempre válido */
  if (!monthStart.ok) return { daily: 0, weekly: 0, monthly: 0, workingDays: 0 };

  const lastDay = new Date(Date.UTC(year!, month, 0)).getUTCDate();
  const monthEnd = BusinessDate.fromParts(year!, month!, lastDay);
  /* c8 ignore next -- idem para o último */
  if (!monthEnd.ok) return { daily: 0, weekly: 0, monthly: 0, workingDays: 0 };

  return {
    daily: capacityOn(resolved, reference),
    weekly: capacityBetween(resolved, weekStart, weekEnd),
    monthly: capacityBetween(resolved, monthStart.value, monthEnd.value),
    workingDays: workingDaysBetween(resolved, monthStart.value, monthEnd.value),
  };
}

/** `36` → `"9h"`; `16` → `"4h"`; `38` → `"9h30"`. Exibição, sem perder o inteiro. */
export function formatQuarterHours(quarterHours: number): string {
  return Hours.ofQuarters(quarterHours).format();
}
